import { fetchPartnerWorkers } from '@/api/partner-workers';
import Pressable from '@/components/motion/pressable';
import { Spinner } from '@/components/ui/spinner';
import { ROUTES } from '@/app/routes';
import type { IPartnerWorker } from '@/entities/admin-user.entity';
import { PageScroll } from '@/layouts/page-layout';
import LandingsEmptyState from '@/pages/landings/components/landings-empty-state';
import LandingsErrorState from '@/pages/landings/components/landings-error-state';
import { ChevronRightIcon, UsersIcon } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

export default function PartnerWorkersPage() {
	const navigate = useNavigate();
	const [workers, setWorkers] = useState<IPartnerWorker[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);

	const load = useCallback(async (showSpinner = false) => {
		if (showSpinner) setLoading(true);
		setError(false);
		try {
			const data = await fetchPartnerWorkers();
			setWorkers(data);
		} catch {
			setError(true);
		} finally {
			if (showSpinner) setLoading(false);
		}
	}, []);

	useEffect(() => {
		void load(true);
	}, [load]);

	return (
		<PageScroll contentClassName='p-2 flex flex-col gap-3'>
			<div>
				<h1 className='text-lg font-semibold'>Воркеры</h1>
				<p className='text-xs text-muted-foreground mt-0.5'>Привязанные воркеры и их статистика</p>
			</div>

			{loading ? (
				<div className='flex justify-center py-16'>
					<Spinner />
				</div>
			) : error ? (
				<LandingsErrorState message='Не удалось загрузить список воркеров.' onRetry={() => void load(true)} />
			) : workers.length === 0 ? (
				<LandingsEmptyState
					icon={UsersIcon}
					title='Нет привязанных воркеров'
					description='Обратитесь к администратору для назначения воркеров.'
				/>
			) : (
				<div className='flex flex-col gap-2'>
					{workers.map(worker => (
						<Pressable
							key={worker.profileId}
							className='cursor-pointer rounded-xl border border-border bg-card px-3 py-3'
							onClick={() => navigate(`${ROUTES.worker}${worker.profileId}`)}
						>
							<div className='flex items-start justify-between gap-2'>
								<div className='min-w-0 flex-1'>
									<p className='font-semibold text-sm'>#{worker.nickname}</p>
									<p className='text-xs text-muted-foreground mt-1'>
										Клиенты: {worker.onlineClientsCount} / {worker.clientsCount}
									</p>
								</div>
								<ChevronRightIcon size={16} className='text-muted-foreground shrink-0 mt-0.5' />
							</div>
						</Pressable>
					))}
				</div>
			)}
		</PageScroll>
	);
}
